/**
 * Smart Edge Component
 * Intelligent edge routing that avoids nodes
 */

import React, { useState } from 'react';
import {
  EdgeLabelRenderer,
  EdgeProps,
  useNodes,
  getSmoothStepPath,
} from 'reactflow';
import { getSmartEdge } from '@tisoap/react-flow-smart-edge';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useWorkflowStore } from '@flow-studio/store'; // import { workflowStore';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { EdgeEditModal } from '../Modals/EdgeEditModal';
import { PencilIcon } from '@bizfirst/common-react';
import type { WorkflowNodeData } from '@flow-studio/types';

export const SmartEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  label,
  selected,
  source,
  target,
  data,
}) => {
  const { handleEdgeContextMenu } = useContextMenu();
  const { deleteEdge } = useWorkflowStore();
  const { theme } = useUIStore();
  const { setSelectedEdges, setSelectedNodes, setSelectedHandle } = useUIStore();
  const [showEditModal, setShowEditModal] = useState(false);
  const nodes = useNodes<WorkflowNodeData>();

  // Check if edge connects to/from satellite nodes
  const sourceNode = nodes.find(n => n.id === source);
  const targetNode = nodes.find(n => n.id === target);
  const isSatelliteEdge = sourceNode?.data?.isSatellite || targetNode?.data?.isSatellite;

  const handleEdgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedEdges([id]);
    setSelectedNodes([]);
    setSelectedHandle(null);
  };

  // Get smart edge path that avoids nodes
  const smartEdgeResult = getSmartEdge({
    sourcePosition,
    targetPosition,
    sourceX,
    sourceY,
    targetX,
    targetY,
    nodes: nodes as any,
    options: {
      nodePadding: 60, // Distance to keep from nodes (increased for more obvious avoidance)
      gridRatio: 15, // Grid size for pathfinding (smaller = more precise path)
    },
  });

  // If smart edge calculation fails, fall back to smooth step
  if (!smartEdgeResult) {
    console.warn('Smart edge calculation failed for edge:', id, 'Falling back to smooth step');
    const [fallbackPath] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    return (
      <>
        <path
          d={fallbackPath}
          fill="none"
          strokeWidth={20}
          stroke="transparent"
          className="edge-interaction-path"
          onClick={handleEdgeClick}
          style={{ cursor: 'pointer' }}
        />
        <path
          d={fallbackPath}
          style={{
            ...style,
            stroke: selected ? '#3b82f6' : (style.stroke || '#9ca3af'),
            strokeWidth: selected ? 3 : (style.strokeWidth || 2),
            fill: 'none',
          }}
          markerEnd={markerEnd}
        />
      </>
    );
  }

  const { svgPathString, labelX, labelY } = smartEdgeResult as any;

  const edgeStyle: React.CSSProperties = {
    ...style,
    stroke: selected ? '#3b82f6' : (style.stroke || '#10b981'),
    strokeWidth: selected ? 3 : (style.strokeWidth || 2),
    fill: 'none',
    strokeDasharray: isSatelliteEdge ? '4, 4' : '5,5', // Dotted for satellite, dashed for smart edges
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    deleteEdge(id);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowEditModal(true);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent edge selection on first click
  };

  return (
    <>
      {/* Invisible wider path for easier clicking */}
      <path
        d={svgPathString}
        fill="none"
        strokeWidth={20}
        stroke="transparent"
        className="edge-interaction-path"
        onClick={handleEdgeClick}
        style={{ cursor: 'pointer' }}
      />

      {/* Visible edge */}
      <path
        d={svgPathString}
        style={edgeStyle}
        markerEnd={markerEnd}
      />

      <EdgeLabelRenderer>
        {/* Label */}
        {label && (
          <div
            className={`edge-label ${theme === 'light' ? 'light' : ''}`}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
            onContextMenu={(e) => handleEdgeContextMenu(e, id, data)}
          >
            {label}
          </div>
        )}

        {/* Action Buttons (visible when selected) */}
        {selected && (
          <div
            className="edge-button-container nodrag nopan"
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            }}
          >
            {/* Edit Button */}
            <button
              className={`edge-button edit ${theme === 'light' ? 'light' : ''}`}
              onClick={handleEdit}
              onMouseDown={handleMouseDown}
              title="Edit connection"
            >
              <PencilIcon size={9} />
            </button>

            {/* Delete Button */}
            <button
              className={`edge-button delete ${theme === 'light' ? 'light' : ''}`}
              onClick={handleDelete}
              onMouseDown={handleMouseDown}
              title="Delete connection"
            >
              ×
            </button>
          </div>
        )}
      </EdgeLabelRenderer>

      {/* Edit Modal */}
      {showEditModal && (
        <EdgeEditModal
          edgeId={id}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </>
  );
};
